import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, MessageCircle, Share2, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import { Reveal, GoldLine } from "@/components/Reveal";
import { fetchProduct, fetchShopifyProduct, toggleWishlistItem, isWishlisted, openBooking } from "@/lib/api";
import { addToBag, openBag } from "@/lib/bag";
import { waLink, WA_MESSAGES } from "@/config/site";

const Spec = ({ label, value }) =>
    value ? (
        <div className="flex justify-between gap-6 py-3 border-b border-gold/10">
            <span className="text-[11px] tracking-[0.22em] uppercase text-sand">{label}</span>
            <span className="text-sm text-ivory text-right">{value}</span>
        </div>
    ) : null;

const ProductDetail = () => {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [active, setActive] = useState(0);
    const [saved, setSaved] = useState(false);
    const [zoom, setZoom] = useState(false);
    const [origin, setOrigin] = useState("50% 50%");
    const [copied, setCopied] = useState(false);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setData(null);
        setActive(0);
        setNotFound(false);
        const apply = (d) => {
            setData(d);
            setSaved(isWishlisted(d.product.slug));
        };
        fetchProduct(slug)
            .then(apply)
            .catch(() =>
                fetchShopifyProduct(slug)
                    .then(apply)
                    .catch(() => setNotFound(true))
            );
    }, [slug]);

    if (notFound)
        return (
            <main className="pt-40 pb-32 text-center min-h-screen">
                <p className="font-serif italic text-3xl text-sand">This piece is no longer in the showcase.</p>
                <Link to="/collections" className="inline-block mt-8 text-gold text-xs tracking-[0.25em] uppercase border-b border-gold/50 pb-1" data-testid="product-back-link">
                    Explore Collections →
                </Link>
            </main>
        );

    if (!data) return <main className="min-h-screen pt-40 text-center text-sand text-sm tracking-[0.3em] uppercase">Unveiling…</main>;

    const { product: p, related } = data;
    const pageUrl = window.location.href;

    const shareWhatsApp = waLink(`${p.name} — BSK Jewellers, Amritsar\n${pageUrl}`);
    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(pageUrl);
            setCopied(true);
            toast.success("Link copied");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Could not copy link");
        }
    };
    const wish = () => {
        const now = toggleWishlistItem(p);
        setSaved(now);
        toast.success(now ? "Saved to your wishlist" : "Removed from wishlist");
    };

    return (
        <main className="pt-28 sm:pt-36 pb-24 min-h-screen">
            <Seo
                title={`${p.name} | BSK Jewellers Amritsar`}
                siteName="BSK Jewellers"
                description={p.description}
                image={p.image}
                type="product"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Product",
                    name: p.name,
                    description: p.description,
                    category: p.category,
                    brand: { "@type": "Brand", name: "BSK Jewellers" },
                }}
            />
            <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
                <nav className="text-[10px] tracking-[0.25em] uppercase text-sand mb-10" aria-label="Breadcrumb" data-testid="product-breadcrumb">
                    <Link to="/" className="hover:text-gold transition-colors">Home</Link>
                    <span className="mx-2 text-gold/50">/</span>
                    <Link to="/collections" className="hover:text-gold transition-colors">Collections</Link>
                    <span className="mx-2 text-gold/50">/</span>
                    <span className="text-gold">{p.name}</span>
                </nav>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
                    <div>
                        <motion.div
                            initial={{ opacity: 0, scale: 1.02 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.7, ease: [0.22, 0.6, 0.24, 1] }}
                            className="relative aspect-[3/4] overflow-hidden border border-gold/20 bg-charcoal"
                            onMouseEnter={() => setZoom(true)}
                            onMouseLeave={() => setZoom(false)}
                            onMouseMove={(e) => {
                                const r = e.currentTarget.getBoundingClientRect();
                                setOrigin(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
                            }}
                            data-testid="product-image-main"
                        >
                            <img
                                src={p.gallery[active]}
                                alt={`${p.name} — view ${active + 1}`}
                                className="w-full h-full object-cover transition-transform duration-500"
                                style={{ transform: zoom ? "scale(1.8)" : "scale(1)", transformOrigin: origin }}
                            />
                        </motion.div>
                        {p.gallery.length > 1 && (
                            <div className="flex gap-3 mt-4">
                                {p.gallery.map((g, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActive(i)}
                                        data-testid={`product-thumb-${i}`}
                                        aria-label={`View ${i + 1}`}
                                        className={`w-20 aspect-square overflow-hidden border transition-colors ${active === i ? "border-gold" : "border-gold/15 hover:border-gold/50"}`}
                                    >
                                        <img src={g} alt="" className="w-full h-full object-cover" loading="lazy" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="lg:pt-4">
                        <Reveal>
                            <span className="eyebrow">{p.category} · {p.collections.join(" / ")}</span>
                            <h1 className="font-serif text-4xl sm:text-5xl leading-[1.08] mt-4" data-testid="product-name">{p.name}</h1>
                            <GoldLine delay={0.15} className="w-20 my-6" />
                            <p className="text-sand text-sm sm:text-base leading-relaxed max-w-lg" data-testid="product-description">{p.description}</p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <div className="mt-8 max-w-lg" data-testid="product-specs">
                                <Spec label="Material" value={p.material} />
                                <Spec label="Purity" value={p.purity} />
                                <Spec label="Weight" value={p.weight} />
                                <Spec label="Stone Details" value={p.stones} />
                                <Spec label="Price" value={p.price_note} />
                            </div>
                            <p className="text-[11px] text-sand/70 italic mt-4 max-w-lg">
                                Product availability and pricing may vary. Please contact BSK Jewellers for current details.
                            </p>
                        </Reveal>
                        <Reveal delay={0.25}>
                            <div className="flex flex-col sm:flex-row gap-3 mt-9 max-w-lg">
                                {p.variant_id && (
                                    <button
                                        onClick={() => {
                                            addToBag(p);
                                            toast.success("Added to your bag");
                                            openBag();
                                        }}
                                        data-testid="product-add-to-bag-btn"
                                        className="flex-1 bg-ivory text-obsidian text-[11px] tracking-[0.22em] uppercase py-4 text-center hover:bg-gold-bright transition-colors"
                                    >
                                        Add to Bag
                                    </button>
                                )}
                                <a
                                    href={waLink(WA_MESSAGES.product(p.name))}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="product-enquire-whatsapp-btn"
                                    className="flex-1 bg-gold text-obsidian text-[11px] tracking-[0.22em] uppercase py-4 text-center hover:bg-gold-bright transition-colors flex items-center justify-center gap-2"
                                >
                                    <MessageCircle size={15} /> Enquire on WhatsApp
                                </a>
                                <button
                                    onClick={() => openBooking(p.category === "necklaces" && p.collections.includes("bridal") ? "Bridal Jewellery" : undefined)}
                                    data-testid="product-book-visit-btn"
                                    className="flex-1 border border-gold/50 text-gold text-[11px] tracking-[0.22em] uppercase py-4 hover:bg-gold hover:text-obsidian transition-colors"
                                >
                                    Book a Store Visit
                                </button>
                            </div>
                            <div className="flex items-center gap-6 mt-7 max-w-lg">
                                <button onClick={wish} data-testid="product-wishlist-btn" className={`flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase transition-colors ${saved ? "text-gold" : "text-sand hover:text-gold"}`}>
                                    <Heart size={15} fill={saved ? "currentColor" : "none"} strokeWidth={1.5} /> {saved ? "Saved" : "Save to Wishlist"}
                                </button>
                                <span className="flex items-center gap-3 text-sand">
                                    <Share2 size={14} strokeWidth={1.5} className="text-gold" />
                                    <a href={shareWhatsApp} target="_blank" rel="noreferrer" data-testid="product-share-whatsapp" className="text-[11px] tracking-[0.2em] uppercase hover:text-gold transition-colors">WhatsApp</a>
                                    <button onClick={copyLink} data-testid="product-copy-link" className="flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase hover:text-gold transition-colors">
                                        {copied ? <Check size={13} /> : <Link2 size={13} />} {copied ? "Copied" : "Copy Link"}
                                    </button>
                                </span>
                            </div>
                        </Reveal>
                    </div>
                </div>

                {related.length > 0 && (
                    <section className="mt-28" data-testid="related-products">
                        <Reveal>
                            <h2 className="font-serif text-3xl sm:text-4xl">You May Also <span className="italic gold-text">Like</span></h2>
                            <GoldLine delay={0.1} className="w-16 my-6" />
                        </Reveal>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
                            {related.map((r, i) => (
                                <Reveal key={r.slug} delay={i * 0.07}>
                                    <ProductCard product={r} />
                                </Reveal>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </main>
    );
};

export default ProductDetail;
